import { groupBy, orderBy } from 'lodash';
import { lruMemoize } from 'reselect';


import { productTitle } from '@src/components/products';
import { categoryTitle } from '@src/components/sales/helpers';
import {
    isProductsLoading,
    getProducts,
} from '@src/selectors';

const isProdudctUpdatesDropdownDataLoading = lruMemoize(
    (productsData, isCategoriesLoading) => isProductsLoading(productsData) || isCategoriesLoading,
);

const getProductUpdatesDropdownData = lruMemoize(
    (productsData, isCategoriesLoading, categories) => {
        if (isProdudctUpdatesDropdownDataLoading(productsData, isCategoriesLoading)) {
            return [];
        }

        const products = getProducts(productsData).toJS();

        return products.map((product) => ({
            id: product.slug,
            selected: false,
            title: productTitle(product),
            type: 'product',
        })).concat(categories.map((category) => ({
            id: category.slug,
            selected: false,
            title: categoryTitle(category),
            type: 'tax',
        })));
    },
);

// Extended helpers for GroupedDropdown (Platforms, Products, Categories)
const isProductNewsGroupedDropdownDataLoading = lruMemoize(
    (productsData, isCategoriesLoading, isPlatformsLoading) => (
        isProductsLoading(productsData) || isCategoriesLoading || isPlatformsLoading
    ),
);

const getProductNewsGroupedDropdownData = lruMemoize(
    (productsData, isCategoriesLoading, categories, platforms = []) => {
        if (isProdudctUpdatesDropdownDataLoading(productsData, isCategoriesLoading)) {
            // Return groups with whatever is available; consumer can still show loading state
        }

        const productsList = getProducts(productsData).toJS();

        const platformItems = (platforms || []).map((platform) => ({
            id: platform.slug,
            title: platform.name,
            type: 'platform',
        }));

        // Group products by specific product lines and platforms
        const groups = [
            {
                name: 'Foremost 5.0 (Agent360)',
                items: productsList.filter((p) => p.product_line === 'Foremost 5.0 Agent360'),
            },
            {
                name: 'Foremost Signature (Agent360)',
                items: productsList.filter((p) => p.platform === 'agent360' && p.product_line === 'Foremost Signature Agent360'),
            },
            {
                name: 'Foremost Signature (ARS)',
                items: productsList.filter((p) => p.platform === 'ars'),
            },
        ];

        // Identify any "Other" products that don't fit the above groups
        const categorizedSlugs = new Set();
        groups.forEach((g) => g.items.forEach((p) => categorizedSlugs.add(p.slug)));
        const otherProducts = productsList.filter((p) => !categorizedSlugs.has(p.slug));

        if (otherProducts.length > 0) {
            groups.push({
                name: 'Other',
                items: otherProducts,
            });
        }

        const productSubgroups = groups
            .filter((group) => group.items.length > 0)
            .map((group) => ({
                group: group.name,
                items: orderBy(group.items, (p) => productTitle(p, { noHtml: true }))
                    .map((product) => ({
                        id: product.slug,
                        title: productTitle(product),
                        type: 'product',
                    })),
            }));

        const categoryItems = (categories || []).map((category) => ({
            id: category.slug,
            title: categoryTitle(category),
            type: 'tax',
        }));

        return [
            { group: 'By Platform', items: platformItems },
            { group: 'By Product', groups: productSubgroups },
            { group: 'By Category', items: categoryItems },
        ];
    },
);

const getProductNewsFlatDropdownItems = lruMemoize(
    (productsData, isCategoriesLoading, categories, platforms = []) => {
        const groups = getProductNewsGroupedDropdownData(
            productsData,
            isCategoriesLoading,
            categories,
            platforms,
        );

        const flattenItems = (groupList) => {
            return groupList.reduce((acc, group) => {
                let items = [];
                if (group.items) {
                    items = items.concat(group.items);
                }
                if (group.groups) {
                    items = items.concat(flattenItems(group.groups));
                }
                return acc.concat(items);
            }, []);
        };

        return flattenItems(groups);
    },
);

export {
    isProdudctUpdatesDropdownDataLoading,
    getProductUpdatesDropdownData,
    isProductNewsGroupedDropdownDataLoading,
    getProductNewsGroupedDropdownData,
    getProductNewsFlatDropdownItems,
};
